import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const EASE = [0.16, 1, 0.3, 1];
const SPACES_LEFT = 2;
const APPLY_URL = "https://retreats.suru.music/#apply";
const APPLY_LABEL = "retreats.suru.music";
const RETREAT_DATES = "26 Sep – 3 Oct 2026";
const DEADLINE = "Friday 12 Sep 2026";

const IMG = {
  invitation: "/retreat/wehbba.webp",
  sessions: "/retreat/sessions.webp",
  community: "/retreat/community.webp",
  studio: "/retreat/studio_group.webp",
  crew: "/retreat/crew.webp",
  calm: "/retreat/bowls.webp",
};

/* ----- shared bits ----- */

const Eyebrow = ({ children, dark }) => (
  <span
    className={`font-display text-[11px] md:text-xs font-light tracking-[0.42em] uppercase ${
      dark ? "text-white/60" : "text-[#73806C]"
    }`}
  >
    {children}
  </span>
);

const rise = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: EASE, delay: 0.15 + i * 0.14 },
  }),
};

/* ----- Slide 1 : The invitation ----- */
const SlideInvitation = () => (
  <div className="relative h-full w-full overflow-hidden grain" data-testid="slide-invitation">
    <motion.div
      className="absolute inset-0"
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 6, ease: EASE }}
    >
      <img
        src={IMG.invitation}
        alt="Mentorship session at the Suru retreat studio in Mallorca"
        className="h-full w-full object-cover object-center"
      />
    </motion.div>
    <div className="absolute inset-0 bg-black/45" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25" />

    <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
      <motion.div variants={rise} initial="hidden" animate="show" custom={0}>
        <Eyebrow dark>The Suru Expansion Retreat · Mallorca</Eyebrow>
      </motion.div>

      <motion.h1
        variants={rise}
        initial="hidden"
        animate="show"
        custom={1}
        className="font-display mt-8 max-w-4xl text-4xl font-extralight leading-[1.15] tracking-[0.14em] text-white sm:text-5xl md:text-6xl md:tracking-[0.18em]"
      >
        EXPAND YOUR SOUND.
        <br />
        EXPAND YOURSELF.
      </motion.h1>

      <motion.p
        variants={rise}
        initial="hidden"
        animate="show"
        custom={2}
        className="mt-10 max-w-xl text-base font-light leading-loose tracking-wide text-white/80 md:text-lg"
      >
        If this retreat has been speaking to something in you, this is your
        invitation to take the next step.
      </motion.p>
    </div>
  </div>
);

/* ----- Slide : The sessions (learn from the gurus) ----- */
const gurus = [
  { name: "TIMO MAAS", role: "Grammy-nominated producer", img: "/retreat/guru_timo.webp" },
  { name: "DAN DUNCAN", role: "Co-founder, Pig&Dan", img: "/retreat/guru_dan.webp" },
  { name: "CHRIS SPERO", role: "Dense & Pika · Moat", img: "/retreat/guru_chris.webp" },
  { name: "WEHBBA", role: "Drumcode · global techno", img: "/retreat/guru_wehbba.webp" },
  { name: "ALEX NOAKES", role: "Live & modular synthesis", img: "/retreat/guru_alex.webp" },
  { name: "MARIA K RICHTERS", role: "Artist development", img: "/retreat/guru_maria.webp" },
];

const SlideSessions = () => (
  <div className="grid h-full w-full grid-cols-1 md:grid-cols-5" data-testid="slide-sessions">
    <div className="relative flex flex-col justify-center bg-[#F7F6F2] px-8 py-16 md:col-span-2 md:px-14 lg:px-20">
      <motion.div variants={rise} initial="hidden" animate="show" custom={0}>
        <Eyebrow>The sessions · expert-led</Eyebrow>
      </motion.div>

      <motion.h2
        variants={rise}
        initial="hidden"
        animate="show"
        custom={1}
        className="font-display mt-8 text-3xl font-extralight leading-[1.2] tracking-[0.08em] text-[#111311] sm:text-4xl lg:text-5xl"
      >
        Learn directly from the gurus.
      </motion.h2>

      <motion.p
        variants={rise}
        initial="hidden"
        animate="show"
        custom={2}
        className="mt-10 max-w-md text-[15px] font-light leading-loose tracking-wide text-[#636861]"
      >
        Producers who’ve shaped the global electronic scene. Not theory —
        real conversation, real experience, shared face to face.
      </motion.p>

      <motion.div
        variants={rise}
        initial="hidden"
        animate="show"
        custom={3}
        className="mt-12 border-t border-[#111311]/10 pt-8"
        data-testid="guru-strip"
      >
        <span className="font-display text-[10px] font-light uppercase tracking-[0.34em] text-[#73806C]">
          The gurus
        </span>
        <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-5">
          {gurus.map((g) => (
            <li key={g.name} className="flex items-center gap-3">
              <img
                src={g.img}
                alt={g.name}
                className="h-11 w-11 flex-shrink-0 rounded-full object-cover grayscale ring-1 ring-[#111311]/10"
              />
              <span className="min-w-0">
                <span className="block truncate font-display text-[13px] font-light tracking-[0.12em] text-[#111311]">
                  {g.name}
                </span>
                <span className="block truncate text-[11px] font-light tracking-wide text-[#636861]">
                  {g.role}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>

    <div className="relative hidden overflow-hidden md:col-span-3 md:block">
      <motion.img
        src={IMG.sessions}
        alt="A live production session at the Suru retreat in Mallorca"
        className="h-full w-full object-cover object-center"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 6, ease: EASE }}
      />
      <div className="absolute inset-0 bg-[#111311]/10" />
    </div>
  </div>
);

/* ----- Slide : The Mallorca week (immersive) ----- */
const SlideMallorca = () => (
  <div className="relative h-full w-full overflow-hidden grain" data-testid="slide-mallorca">
    <motion.div
      className="absolute inset-0"
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 6, ease: EASE }}
    >
      <img
        src={IMG.calm}
        alt="A calm sound and stillness space at the Mallorca retreat"
        className="h-full w-full object-cover object-center"
      />
    </motion.div>
    <div className="absolute inset-0 bg-black/50" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

    <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
      <motion.div variants={rise} initial="hidden" animate="show" custom={0}>
        <Eyebrow dark>26 Sep – 3 Oct 2026 · Mallorca</Eyebrow>
      </motion.div>

      <motion.h2
        variants={rise}
        initial="hidden"
        animate="show"
        custom={1}
        className="font-display mt-8 max-w-3xl text-4xl font-extralight leading-[1.15] tracking-[0.12em] text-white sm:text-5xl md:tracking-[0.16em]"
      >
        A week away from the noise.
      </motion.h2>

      <motion.p
        variants={rise}
        initial="hidden"
        animate="show"
        custom={2}
        className="mt-10 max-w-xl text-base font-light leading-loose tracking-wide text-white/80 md:text-lg"
      >
        Sun-soaked serenity, immersive workshops, and space to hear what’s
        possible — built to reignite your inner artist.
      </motion.p>
    </div>
  </div>
);


/* ----- Slide 2 : Why act now ----- */
const SlideUrgency = () => (
  <div className="grid h-full w-full grid-cols-1 md:grid-cols-2" data-testid="slide-urgency">
    <div className="relative flex flex-col justify-center bg-[#111311] px-8 py-16 md:px-16 lg:px-24">
      <motion.div variants={rise} initial="hidden" animate="show" custom={0}>
        <Eyebrow dark>Applications are open</Eyebrow>
      </motion.div>

      <motion.h2
        variants={rise}
        initial="hidden"
        animate="show"
        custom={1}
        className="font-display mt-8 text-3xl font-extralight leading-[1.2] tracking-[0.1em] text-white sm:text-4xl lg:text-5xl"
      >
        Limited spaces.
        <br />
        First come,
        <br />
        first served.
      </motion.h2>

      <motion.div
        variants={rise}
        initial="hidden"
        animate="show"
        custom={2}
        className="mt-10 flex items-center gap-4"
      >
        <span className="font-display text-5xl font-extralight text-[#A6B39B]" data-testid="spaces-left">
          {SPACES_LEFT}
        </span>
        <span className="text-sm font-light uppercase tracking-[0.28em] text-white/50">
          spaces
          <br />
          remaining
        </span>
      </motion.div>

      <motion.p
        variants={rise}
        initial="hidden"
        animate="show"
        custom={3}
        className="mt-10 max-w-md text-[15px] font-light leading-loose tracking-wide text-white/70"
      >
        Applying is the next step. When the house fills, this year’s
        participants share a{" "}
        <span className="text-white/90 underline decoration-[#73806C] decoration-1 underline-offset-4">
          private guided production session with Dan
        </span>
        .
      </motion.p>

      <motion.div
        variants={rise}
        initial="hidden"
        animate="show"
        custom={4}
        className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-8 text-white/70 sm:flex-row sm:gap-10"
        data-testid="dates-block"
      >
        <div>
          <span className="block font-display text-[10px] uppercase tracking-[0.3em] text-white/40">
            Retreat
          </span>
          <span className="mt-1 block text-sm font-light tracking-wide">
            {RETREAT_DATES} · Mallorca
          </span>
        </div>
        <div>
          <span className="block font-display text-[10px] uppercase tracking-[0.3em] text-[#A6B39B]">
            Applications close
          </span>
          <span className="mt-1 block text-sm font-light tracking-wide" data-testid="deadline">
            This {DEADLINE}
          </span>
        </div>
      </motion.div>
    </div>

    <div className="relative hidden overflow-hidden md:block">
      <motion.img
        src={IMG.studio}
        alt="The retreat cohort together in the Mallorca studio"
        className="h-full w-full object-cover object-top"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 6, ease: EASE }}
      />
      <div className="absolute inset-0 bg-[#111311]/25" />
    </div>
  </div>
);

/* ----- Slide 3 : What they're stepping into ----- */
const bullets = [
  "Deep creative expansion",
  "Direct access to Suru mentors",
  "A serious artist community",
  "A private guided production session with Dan",
];

const SlideValue = () => (
  <div className="grid h-full w-full grid-cols-1 md:grid-cols-5" data-testid="slide-value">
    <div className="relative flex flex-col justify-center bg-[#F7F6F2] px-8 py-16 md:col-span-3 md:px-16 lg:px-24">
      <motion.div variants={rise} initial="hidden" animate="show" custom={0}>
        <Eyebrow>What you’re stepping into</Eyebrow>
      </motion.div>

      <motion.h2
        variants={rise}
        initial="hidden"
        animate="show"
        custom={1}
        className="font-display mt-8 max-w-xl text-3xl font-extralight leading-[1.2] tracking-[0.08em] text-[#111311] sm:text-4xl lg:text-5xl"
      >
        More than a place on the retreat.
      </motion.h2>

      <motion.ul
        className="mt-12 max-w-xl"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.16, delayChildren: 0.4 } } }}
      >
        {bullets.map((b, i) => (
          <motion.li
            key={b}
            variants={{
              hidden: { opacity: 0, y: 18 },
              show: { opacity: 1, y: 0, transition: { duration: 1, ease: EASE } },
            }}
            className="group flex items-center gap-6 border-b border-[#111311]/10 py-5"
            data-testid={`value-bullet-${i}`}
          >
            <span className="font-display w-8 text-sm font-light tracking-[0.2em] text-[#73806C]">
              0{i + 1}
            </span>
            <span className="text-base font-light tracking-wide text-[#111311] md:text-lg">
              {b}
            </span>
          </motion.li>
        ))}
      </motion.ul>
    </div>

    <div className="relative hidden overflow-hidden md:col-span-2 md:block">
      <motion.img
        src={IMG.community}
        alt="The Suru retreat community working together at the Mallorca villa"
        className="h-full w-full object-cover object-center"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 6, ease: EASE }}
      />
      <div className="absolute inset-0 bg-[#111311]/10" />
    </div>
  </div>
);

/* ----- Slide 4 : Final CTA ----- */
const SlideCTA = () => (
  <div
    className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#0E100E] px-6 text-center grain"
    data-testid="slide-cta"
  >
    <img
      src={IMG.calm}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-25"
    />
    <div className="absolute inset-0 bg-[#0E100E]/80" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0E100E] via-[#0E100E]/70 to-[#0E100E]/60" />

    <div className="relative z-10 flex flex-col items-center">
    <motion.img
      src="/retreat/suru-logo.webp"
      alt="Suru"
      className="h-24 w-auto object-contain opacity-95 md:h-28"
      variants={rise}
      initial="hidden"
      animate="show"
      custom={0}
    />

    <motion.h2
      variants={rise}
      initial="hidden"
      animate="show"
      custom={1}
      className="font-display mt-6 text-3xl font-extralight leading-[1.2] tracking-[0.1em] text-white sm:text-4xl md:text-5xl"
    >
      Apply through the retreat form.
    </motion.h2>

    <motion.p
      variants={rise}
      initial="hidden"
      animate="show"
      custom={2}
      className="mt-8 max-w-md text-base font-light leading-loose tracking-wide text-white/60"
    >
      If it feels aligned, apply now and we’ll take it from there.
    </motion.p>

    <motion.a
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="apply-now-btn"
      variants={rise}
      initial="hidden"
      animate="show"
      custom={3}
      className="group mt-12 inline-flex items-center gap-3 border border-white/50 px-10 py-4 text-sm font-light uppercase tracking-[0.28em] text-white transition-colors duration-500 hover:border-[#A6B39B] hover:bg-[#73806C] hover:text-white"
    >
      Apply now
      <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
    </motion.a>

    <motion.a
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="apply-url"
      variants={rise}
      initial="hidden"
      animate="show"
      custom={4}
      className="font-display mt-8 text-lg tracking-[0.18em] text-[#C7D1BD] underline-offset-8 hover:underline md:text-xl"
    >
      {APPLY_LABEL}
    </motion.a>

    <motion.div
      variants={rise}
      initial="hidden"
      animate="show"
      custom={5}
      className="mt-12 flex flex-col items-center"
      data-testid="apply-qr"
    >
      <div className="rounded-2xl bg-white p-4 shadow-[0_0_60px_rgba(166,179,155,0.12)]">
        <QRCodeSVG
          value={APPLY_URL}
          size={132}
          level="M"
          bgColor="#ffffff"
          fgColor="#0E100E"
          marginSize={0}
        />
      </div>
      <span className="mt-4 text-[11px] font-light uppercase tracking-[0.3em] text-white/45">
        Scan to apply from your phone
      </span>
    </motion.div>

    <motion.p
      variants={rise}
      initial="hidden"
      animate="show"
      custom={6}
      className="mt-10 text-xs font-light uppercase tracking-[0.28em] text-[#A6B39B]"
    >
      Applications close this {DEADLINE}
    </motion.p>

    <motion.p
      variants={rise}
      initial="hidden"
      animate="show"
      custom={7}
      className="mt-12 text-[11px] font-light uppercase tracking-[0.34em] text-white/40"
    >
      For artists ready to go deeper
    </motion.p>
    </div>
  </div>
);

const slides = [
  SlideInvitation,
  SlideSessions,
  SlideMallorca,
  SlideValue,
  SlideUrgency,
  SlideCTA,
];
const labels = [
  "Invitation",
  "The sessions",
  "Mallorca",
  "The retreat",
  "Why now",
  "Apply",
];

/* ----- Presentation shell ----- */
export default function Presentation() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const paginate = useCallback(
    (next) => {
      setDir(next > index ? 1 : -1);
      setIndex(Math.max(0, Math.min(slides.length - 1, next)));
    },
    [index]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") paginate(index + 1);
      if (e.key === "ArrowLeft") paginate(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, paginate]);

  const Active = slides[index];
  const isDark = [0, 2, 4, 5].includes(index);

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -60 : 60 }),
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#F7F6F2]" data-testid="presentation">
      {/* top progress line */}
      <div className="absolute left-0 right-0 top-0 z-30 flex">
        {slides.map((_, i) => (
          <div key={i} className="h-[3px] flex-1 bg-black/5">
            <motion.div
              className="h-full bg-[#73806C]"
              initial={false}
              animate={{ width: i <= index ? "100%" : "0%" }}
              transition={{ duration: 0.7, ease: EASE }}
            />
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait" custom={dir}>
        <motion.div
          key={index}
          custom={dir}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.7, ease: EASE }}
          className="h-full w-full"
        >
          <Active />
        </motion.div>
      </AnimatePresence>

      {/* controls */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-center justify-between px-6 py-7 md:px-10">
        <button
          onClick={() => paginate(index - 1)}
          disabled={index === 0}
          data-testid="prev-slide"
          className={`pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${
            isDark
              ? "border-white/30 text-white hover:bg-white/10"
              : "border-[#111311]/25 text-[#111311] hover:bg-[#111311]/5"
          } ${index === 0 ? "cursor-not-allowed opacity-25" : ""}`}
          aria-label="Previous slide"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="pointer-events-auto flex items-center gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => paginate(i)}
              data-testid={`dot-${i}`}
              aria-label={`Go to ${labels[i]} slide`}
              className="group flex flex-col items-center gap-2"
            >
              <span
                className={`block h-[6px] rounded-full transition-all duration-500 ${
                  i === index ? "w-8" : "w-[6px]"
                } ${
                  isDark
                    ? i === index
                      ? "bg-white"
                      : "bg-white/40 group-hover:bg-white/70"
                    : i === index
                    ? "bg-[#73806C]"
                    : "bg-[#111311]/25 group-hover:bg-[#111311]/50"
                }`}
              />
            </button>
          ))}
        </div>

        <button
          onClick={() => paginate(index + 1)}
          disabled={index === slides.length - 1}
          data-testid="next-slide"
          className={`pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${
            isDark
              ? "border-white/30 text-white hover:bg-white/10"
              : "border-[#111311]/25 text-[#111311] hover:bg-[#111311]/5"
          } ${index === slides.length - 1 ? "cursor-not-allowed opacity-25" : ""}`}
          aria-label="Next slide"
        >
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
